import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useSocket } from './SocketContext'

/**
 * Shared context that tracks per-group unread message counts.
 * Lives at the app root so state survives page navigation.
 *
 * - Increments per-group count on each incoming `whatsapp_group_message`
 * - Only clears a group's count when `markGroupRead(groupId)` is called
 *   (i.e. when user clicks into that specific group)
 * - Provides `totalUnread` for the sidebar badge
 */

interface GroupUnreadContextType {
  /** Map of groupId → unread message count */
  unreadGroups: Record<string, number>
  /** Total unread across all groups (for sidebar badge) */
  totalUnread: number
  /** Mark a specific group as read (clears its count and sets it as active) */
  markGroupRead: (groupId: string) => void
  /** Clear the active group (call when leaving the groups page) */
  clearActiveGroup: () => void
}

const GroupUnreadContext = createContext<GroupUnreadContextType>({
  unreadGroups: {},
  totalUnread: 0,
  markGroupRead: () => {},
  clearActiveGroup: () => {},
})

export const useGroupUnread = () => useContext(GroupUnreadContext)

export const GroupUnreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { socket } = useSocket()
  const [unreadGroups, setUnreadGroups] = useState<Record<string, number>>({})

  // Track which group the user is currently viewing so we don't count
  // messages for it. This is set by the consumer via markGroupRead.
  const activeGroupRef = useRef<string | null>(null)

  const markGroupRead = useCallback((groupId: string) => {
    activeGroupRef.current = groupId
    setUnreadGroups(prev => {
      if (!prev[groupId]) return prev // nothing to clear
      const next = { ...prev }
      delete next[groupId]
      return next
    })
  }, [])

  const clearActiveGroup = useCallback(() => {
    activeGroupRef.current = null
  }, [])

  useEffect(() => {
    if (!socket) return

    const handleGroupMessage = (payload: any) => {
      if (!payload?.groupId || !payload?.message) return

      // Don't count outgoing messages
      const msg = payload.message
      if (msg.direction === 'OUTGOING') return

      // Don't count if user is currently viewing this group
      if (activeGroupRef.current === payload.groupId) return

      setUnreadGroups(prev => ({
        ...prev,
        [payload.groupId]: (prev[payload.groupId] || 0) + 1,
      }))
    }

    socket.on('whatsapp_group_message', handleGroupMessage)
    return () => {
      socket.off('whatsapp_group_message', handleGroupMessage)
    }
  }, [socket])

  const totalUnread = Object.values(unreadGroups).reduce((sum, n) => sum + n, 0)

  return (
    <GroupUnreadContext.Provider value={{ unreadGroups, totalUnread, markGroupRead, clearActiveGroup }}>
      {children}
    </GroupUnreadContext.Provider>
  )
}
